import { ArrowLeft, Pencil, Trash2, Play, Pause, Zap } from "lucide-react";
import { useState } from "react";

import {
  useAutomation,
  useDeleteAutomation,
  useTriggerAutomation,
  usePauseAutomation,
  useResumeAutomation,
} from "@/hooks/useAutomations";
import { useStore } from "@/lib/store";

import { AutomationModal } from "./AutomationModal";
import { AutomationRunList } from "./AutomationRunList";
import { CronPreview } from "./CronPreview";
import { describeSchedule, relativeTime } from "./utils";

export function AutomationDetail(): JSX.Element | null {
  const automationId = useStore((state) => state.activeAutomationId);
  const setView = useStore((state) => state.setView);
  const showToast = useStore((state) => state.showToast);

  const { data, isLoading } = useAutomation(automationId);
  const deleteAutomation = useDeleteAutomation();
  const triggerAutomation = useTriggerAutomation();
  const pauseAutomation = usePauseAutomation();
  const resumeAutomation = useResumeAutomation();

  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!automationId) return null;

  const automation = data?.automation;
  const runs = data?.runs ?? [];
  const isEnabled = automation?.status === "enabled";

  async function handleToggle() {
    if (!automation) return;
    try {
      if (isEnabled) {
        await pauseAutomation.mutateAsync(automation.id);
        showToast("Automation paused", "success");
      } else {
        await resumeAutomation.mutateAsync(automation.id);
        showToast("Automation resumed", "success");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed", "error");
    }
  }

  async function handleTrigger() {
    if (!automation) return;
    try {
      await triggerAutomation.mutateAsync(automation.id);
      showToast("Automation triggered", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to trigger", "error");
    }
  }

  async function handleDelete() {
    if (!automation) return;
    try {
      await deleteAutomation.mutateAsync(automation.id);
      showToast("Automation deleted", "success");
      setView("automations");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to delete", "error");
    }
    setShowDeleteConfirm(false);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-cyber-border px-4 py-3">
        <div className="flex items-center justify-between gap-3 px-6">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              onClick={() => setView("automations")}
              className="flex-shrink-0 rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
              title="Back to automations"
            >
              <ArrowLeft size={16} />
            </button>

            {isLoading ? (
              <span className="text-sm text-cyber-muted">
                <span className="animate-spin-glow mr-1 inline-block">◐</span>
                Loading...
              </span>
            ) : automation ? (
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-sm font-medium text-cyber-text">
                    {automation.name}
                  </h2>
                  <span
                    className={`flex-shrink-0 rounded border px-1.5 py-0.5 text-[10px] ${
                      isEnabled
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                        : "border-cyber-border bg-cyber-muted/10 text-cyber-muted"
                    }`}
                  >
                    {isEnabled ? "Active" : "Paused"}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="text-[10px] text-cyber-muted">
                    {describeSchedule(automation.schedule)}
                  </span>
                  {automation.target.source === "slack" && (
                    <span className="text-[10px] text-cyber-cyan/60">Slack</span>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {automation && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleTrigger}
                disabled={triggerAutomation.isPending}
                className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
                title="Run now"
              >
                <Zap size={16} />
              </button>
              <button
                onClick={handleToggle}
                disabled={pauseAutomation.isPending || resumeAutomation.isPending}
                className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
                title={isEnabled ? "Pause" : "Resume"}
              >
                {isEnabled ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button
                onClick={() => setShowEdit(true)}
                className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
                title="Edit"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-red/10 hover:text-cyber-red"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {automation && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mx-auto max-w-3xl">
            {/* Info cards */}
            <div className="mb-6 grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
              <div className="rounded-lg border border-cyber-border bg-cyber-surface/50 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-wider text-cyber-muted">
                    Prompt
                  </div>
                  <div className="text-[10px] text-cyber-muted/60">
                    {automation.prompt.length.toLocaleString()} chars
                  </div>
                </div>
                <div className="max-h-[32rem] overflow-y-auto rounded-md border border-cyber-border/60 bg-cyber-bg/40 p-3">
                  <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-cyber-text">
                    {automation.prompt}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-cyber-border bg-cyber-surface/50 p-3">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-cyber-muted">
                  Schedule
                </div>
                <div className="mb-1 text-xs text-cyber-text">
                  {describeSchedule(automation.schedule)}
                </div>
                <CronPreview
                  cron={automation.schedule.cron}
                  timezone={automation.schedule.timezone}
                  variant="detailed"
                />
                <div className="mt-1 text-[10px] text-cyber-muted/60">
                  {automation.schedule.timezone}
                </div>
                <div className="mt-1 text-[10px] text-cyber-muted/60">
                  Model: {automation.modelId ? automation.modelId.split("/").pop() : "Default"}
                </div>
                {automation.lastRunAt && (
                  <div className="mt-1 text-[10px] text-cyber-muted/60">
                    Last run: {relativeTime(automation.lastRunAt)}
                  </div>
                )}
              </div>
            </div>

            {/* Run history */}
            <div>
              <h3 className="mb-3 text-xs uppercase tracking-wider text-cyber-muted">
                Run history
              </h3>
              <AutomationRunList automationId={automation.id} runs={runs} />
            </div>
          </div>
        </div>
      )}

      {showEdit && automation && (
        <AutomationModal automation={automation} onClose={() => setShowEdit(false)} />
      )}

      {showDeleteConfirm && (
        <>
          <div
            role="presentation"
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
            <div className="pointer-events-auto w-72 rounded-lg border border-cyber-border bg-cyber-surface p-4">
              <p className="mb-4 text-sm text-cyber-text">
                Delete &ldquo;{automation?.name}&rdquo;?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-sm text-cyber-muted transition-all hover:text-cyber-text"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteAutomation.isPending}
                  className="rounded border border-cyber-red/30 bg-cyber-red/10 px-3 py-1.5 text-sm text-cyber-red transition-all hover:bg-cyber-red/20 disabled:opacity-50"
                >
                  {deleteAutomation.isPending ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
